import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import API from 'api';
import { apiURL } from 'config';
import Portal from '../Portal';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';

const UPDATE_CHECK_INTERVAL_MS = 15 * 1000;
const CONFIG_CHECK_INTERVAL_MS = 30 * 1000;
const INITIAL_UPDATE_CHECK_DELAY_MS = 3000;
const UPDATE_CHECK_PATH = '_update-check/asset-manifest.json';

const getRemoteFrontendOrigin = () => {
  try {
    return new URL(apiURL).origin;
  } catch (error) {
    return 'https://test.flclogin.com';
  }
};

const normalizeAssetPath = value => {
  if (!value || typeof value !== 'string') return '';
  try {
    return new URL(value, getRemoteFrontendOrigin()).pathname;
  } catch (error) {
    return value.split('?')[0];
  }
};

const stableSerialize = value => {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

const getConfigRevision = config => {
  if (!config || typeof config !== 'object') return '';
  const serialized = stableSerialize(config);
  let hash = 2166136261;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${serialized.length.toString(36)}-${(hash >>> 0).toString(16)}`;
};

const hashText = value => {
  const text = String(value || '');
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
};

export const isNativeMobileUpdateRuntime = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const platform = getMobileAppPlatform();
  if (platform === 'ios' || platform === 'android' || platform === 'native') return true;
  const userAgent = String(navigator.userAgent || '');
  const mobileUserAgent = /Android|iPhone|iPad|iPod/i.test(userAgent);
  const hasCapacitorBridge = Boolean(window.Capacitor || window.androidBridge || window.webkit?.messageHandlers?.bridge);
  if (mobileUserAgent && hasCapacitorBridge) return true;
  const isStandalonePwa = Boolean(navigator.standalone || window.matchMedia?.('(display-mode: standalone)')?.matches);
  if (isStandalonePwa) return false;
  const isAndroidWebView = /;\s*wv\)/i.test(userAgent) || /\bwv\b/i.test(userAgent);
  const isIosWebView = /iPhone|iPad|iPod/i.test(userAgent) && /AppleWebKit/i.test(userAgent) && !/Safari/i.test(userAgent);
  return isAndroidWebView || isIosWebView;
};

export const getLoadedMainAsset = () => {
  if (typeof document === 'undefined') return '';
  const scripts = Array.from(document.scripts || []);
  const mainScript = scripts.find(({ src = '' }) => /\/static\/js\/main\.[^/]+(?:\.chunk)?\.js(?:\?|$)/i.test(src));
  return normalizeAssetPath(mainScript?.src);
};

export const getManifestMainAsset = manifest => {
  const filesMain = manifest?.files?.['main.js'];
  if (filesMain) return normalizeAssetPath(filesMain);
  const entrypoint = (manifest?.entrypoints || []).find(value => /\/static\/js\/main\.[^/]+(?:\.chunk)?\.js(?:\?|$)/i.test(value));
  return normalizeAssetPath(entrypoint);
};

const getNativeStorage = () => window.Capacitor?.Plugins?.FLCOfflineStorage;
const getWebViewPlugin = () => window.Capacitor?.Plugins?.WebView;

const nativeDownloadText = async (url, path) => {
  const storage = getNativeStorage();
  if (!storage?.downloadFile || !storage?.readText) throw new Error('Native update storage is unavailable');
  await storage.downloadFile({ url, path, expectedSize: 0 });
  const result = await storage.readText({ path });
  return String(result?.text || '');
};

const fetchLatestManifest = async () => {
  const origin = getRemoteFrontendOrigin();
  const url = `${origin}/asset-manifest.json?_flcUpdateCheck=${Date.now()}`;
  const text = await nativeDownloadText(url, UPDATE_CHECK_PATH);
  if (!text) throw new Error('Remote asset manifest is empty');
  return JSON.parse(text);
};

const remoteAssetUrl = value => new URL(value, `${getRemoteFrontendOrigin()}/`).toString();
const localAssetPath = value => normalizeAssetPath(value).replace(/^\/+/, '');

const installRemoteBuild = async manifest => {
  const storage = getNativeStorage();
  const webView = getWebViewPlugin();
  if (!storage?.mkdir || !storage?.downloadFile || !webView?.setServerBasePath || !webView?.persistServerBasePath) {
    throw new Error('Native web update support is unavailable');
  }

  const mainAsset = getManifestMainAsset(manifest);
  if (!mainAsset) throw new Error('Remote build has no main.js');
  const buildId = `flc-${hashText(JSON.stringify(manifest?.files || {}))}`;
  const rootRelative = `web-updates/${buildId}`;
  const root = await storage.mkdir({ path: rootRelative });
  if (!root?.path) throw new Error('Unable to create web update directory');

  const required = new Map();
  required.set('index.html', `${getRemoteFrontendOrigin()}/index.html?_flcUpdate=${Date.now()}`);
  required.set('asset-manifest.json', `${getRemoteFrontendOrigin()}/asset-manifest.json?_flcUpdate=${Date.now()}`);

  Object.values(manifest?.files || {}).forEach(value => {
    if (!value || typeof value !== 'string') return;
    const path = localAssetPath(value);
    if (!path || path.endsWith('.map')) return;
    required.set(path, remoteAssetUrl(value));
  });

  for (const optionalPath of ['manifest.json', 'favicon.ico']) {
    required.set(optionalPath, `${getRemoteFrontendOrigin()}/${optionalPath}?_flcUpdate=${Date.now()}`);
  }

  for (const [path, url] of required.entries()) {
    try {
      await storage.downloadFile({ url, path: `${rootRelative}/${path}`, expectedSize: 0 });
    } catch (error) {
      if (path === 'manifest.json' || path === 'favicon.ico') continue;
      throw new Error(`Web update download failed: ${path} - ${error?.message || error}`);
    }
  }

  await webView.setServerBasePath({ path: root.path });
  await webView.persistServerBasePath();
  window.location.replace(`${window.location.origin}/?_flcUpdate=${Date.now()}`);
};

const MobileUpdateAvailable = ({ translate, config }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const checkInProgressRef = useRef(false);
  const configCheckInProgressRef = useRef(false);
  const configRevision = getConfigRevision(config);

  const checkForUpdate = useCallback(async () => {
    if (checkInProgressRef.current || updateAvailable) return;
    checkInProgressRef.current = true;
    try {
      const manifest = await fetchLatestManifest();
      const loadedMainAsset = getLoadedMainAsset();
      const latestMainAsset = getManifestMainAsset(manifest);
      if (loadedMainAsset && latestMainAsset && loadedMainAsset !== latestMainAsset) setUpdateAvailable(true);
    } catch (error) {
      console.warn('[mobile-update] check failed', error?.message || error);
    } finally {
      checkInProgressRef.current = false;
    }
  }, [updateAvailable]);

  const checkForConfigUpdate = useCallback(async () => {
    if (configCheckInProgressRef.current || updateAvailable || !configRevision) return;
    configCheckInProgressRef.current = true;
    try {
      if (await API.mobileWrapper.checkConfigChanged(configRevision)) setUpdateAvailable(true);
    } catch (error) {
      // Network/API failures never block app use.
    } finally {
      configCheckInProgressRef.current = false;
    }
  }, [configRevision, updateAvailable]);

  useEffect(() => {
    if (!isNativeMobileUpdateRuntime()) return undefined;
    const initialTimer = window.setTimeout(checkForUpdate, INITIAL_UPDATE_CHECK_DELAY_MS);
    const interval = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') checkForUpdate(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkForUpdate);
    window.addEventListener('online', checkForUpdate);
    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkForUpdate);
      window.removeEventListener('online', checkForUpdate);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    if (!isNativeMobileUpdateRuntime()) return undefined;
    checkForConfigUpdate();
    const interval = window.setInterval(checkForConfigUpdate, CONFIG_CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [checkForConfigUpdate]);

  const reloadLatestVersion = async () => {
    if (reloading) return;
    setReloading(true);
    setUpdateError('');
    try {
      const manifest = await fetchLatestManifest();
      await installRemoteBuild(manifest);
    } catch (error) {
      console.error('[mobile-update] install failed', error);
      setUpdateError(error?.message || 'Update failed. Please try again.');
      setReloading(false);
    }
  };

  if (!updateAvailable || !isNativeMobileUpdateRuntime()) return null;
  const t = defaultText => (typeof translate === 'function' ? translate(0, defaultText, true) : defaultText);

  return (
    <Portal>
      <UpdateBlocker aria-hidden="true" />
      <UpdatePanel role="status" aria-live="polite">
        <UpdateMark aria-hidden="true">&#8635;</UpdateMark>
        <UpdateCopy>
          <UpdateTitle>{t('Update available')}</UpdateTitle>
          <UpdateText>{updateError || t('Reload to use the latest version.')}</UpdateText>
        </UpdateCopy>
        <ReloadButton type="button" onClick={reloadLatestVersion} disabled={reloading}>
          {reloading ? t('Updating...') : t('Reload')}
        </ReloadButton>
      </UpdatePanel>
    </Portal>
  );
};

MobileUpdateAvailable.propTypes = {
  translate: PropTypes.func,
  config: PropTypes.object,
};

const mapStateToProps = ({ status, flinkPlay, common }) => ({
  translate: flinkPlay?.solutionTranslate || status?.translate,
  config: common?.config,
});

export default connect(mapStateToProps)(MobileUpdateAvailable);

const UpdateBlocker = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3199;
  background: rgba(238, 244, 255, .18);
  -webkit-backdrop-filter: blur(7px) saturate(.88);
  backdrop-filter: blur(7px) saturate(.88);
  pointer-events: auto;
  touch-action: none;
  user-select: none;
`;

const UpdatePanel = styled.div`
  position: fixed;
  z-index: 3200;
  top: max(10px, env(safe-area-inset-top));
  left: 50%;
  transform: translateX(-50%);
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: clamp(10px, 2vw, 18px);
  width: min(680px, calc(100vw - max(28px, env(safe-area-inset-left)) - max(28px, env(safe-area-inset-right))));
  min-height: 58px;
  padding: 10px 12px 10px 14px;
  box-sizing: border-box;
  border: 1px solid rgba(90, 120, 171, .26);
  border-radius: 18px;
  background: rgba(255, 255, 255, .98);
  box-shadow: 0 10px 32px rgba(24, 48, 89, .24);
  color: #17345f;
`;

const UpdateMark = styled.div`
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: linear-gradient(145deg, #8b5cf6, #3b82f6);
  color: #fff;
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
`;

const UpdateCopy = styled.div`
  min-width: 0;
`;

const UpdateTitle = styled.div`
  overflow: hidden;
  color: #17345f;
  font-size: clamp(14px, 2.7vh, 18px);
  font-weight: 800;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UpdateText = styled.div`
  overflow: hidden;
  margin-top: 3px;
  color: #657796;
  font-size: clamp(11px, 2vh, 13px);
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReloadButton = styled.button`
  min-width: 94px;
  min-height: 40px;
  padding: 0 20px;
  border: 0;
  border-radius: 12px;
  background: linear-gradient(145deg, #2563eb, #4f46e5);
  box-shadow: 0 5px 13px rgba(37, 99, 235, .24);
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-size: clamp(13px, 2.4vh, 16px);
  font-weight: 800;

  &:disabled {
    cursor: default;
    opacity: .7;
  }
`;
